/**
 * Client-side image compression.
 * - Downscales to maxDim (longest side) preserving aspect ratio.
 * - Re-encodes as JPEG at given quality.
 * - Handles EXIF rotation via createImageBitmap when available (modern browsers).
 * - Keeps original File if it is already small enough.
 *
 * Why: Android cameras often return 5-20+ MB JPEGs which make uploads slow and
 * can hit network timeouts on mobile data. Browsers do not auto-compress on Android
 * (Safari on iOS tends to send ~2-4 MB).
 */
export async function compressImage(file, { maxDim = 1600, quality = 0.85, skipUnderMB = 1.5 } = {}) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  // Skip HEIC: browsers can't decode it on canvas; let the backend keep as-is.
  if (/heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name || "")) return file;
  // Skip tiny files — no benefit compressing them further.
  if (file.size <= skipUnderMB * 1024 * 1024) return file;

  try {
    const bitmap = await loadBitmap(file);
    const { width: w, height: h } = bitmap;
    const scale = Math.min(1, maxDim / Math.max(w, h));
    const tw = Math.round(w * scale);
    const th = Math.round(h * scale);
    const canvas = document.createElement("canvas");
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, tw, th);
    if (bitmap.close) bitmap.close();

    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob) return file;
    // If the "compressed" version came out bigger (rare), keep the original.
    if (blob.size >= file.size) return file;
    const outName = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], outName, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    // Any failure (e.g. OOM on very old Androids) → fall back to original file.
    return file;
  }
}

async function loadBitmap(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch { /* fall through to <img> fallback */ }
  }
  return await new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      // Expose width/height like ImageBitmap so the caller works unchanged.
      img.width = img.naturalWidth;
      img.height = img.naturalHeight;
      resolve(img);
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}
